import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import InnerPaddingSectionWrapper from '../wrapper/InnerPaddingSectionWrapper';
import Select from '../components/ui/Select';
import ImageSkeleton from '../components/ui/ImageSkeleton';
import { routes } from '../constants/routes';
import Pagination from '../components/ui/Pagination';
import { getAllProductList } from '../api/products';
import Meta from '../../components/seo/Meta';
import { SITE_URL } from '../../constants/seo';

export default function Shop() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [productItems, setProductItems] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 0,
    totalPage: 1,
  });
  const [isLoading, setIsLoading] = useState(false); // 로딩 상태 추가

  const sort = searchParams.get('sort') || 'latest';
  const premium = searchParams.get('premium') || '';
  const page = searchParams.get('page') || '0';

  useEffect(() => {
    setIsLoading(true); // 데이터 로딩 시작
    window.scrollTo(0, 0); // 스크롤 최상단으로
    getAllProductList(sort, page)
      .then(res => {
        const items = res?.products || [];
        setProductItems(items);

        const paginationData = res?.pagination || {
          currentPage: 0,
          totalPage: 1,
        };
        setPagination(paginationData);
      })
      .catch(error => {
        console.error('상품 목록을 불러오는데 실패했습니다:', error);
        setProductItems([]);
        setPagination({ currentPage: 0, totalPage: 1 });
      })
      .finally(() => setIsLoading(false)); // 데이터 로딩 완료
  }, [sort, page, premium]);

  const sortOptions = [
    { value: 'latest', label: '최신순' },
    { value: 'popular', label: '인기순' },
    { value: 'review', label: '리뷰많은순' },
  ];

  const handleSortChange = newSort => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('sort', newSort);
      newParams.set('page', '0');
      return newParams;
    });
  };

  return (
    <>
      <Meta
        title='Shop | 몽솜'
        description='몽솜 인기·최신 선물 모아보기. 답례품, 기업/단체 대량구매 가능.'
        canonical={`${SITE_URL}/shop`}
      />
      <InnerPaddingSectionWrapper>
        <div className='flex items-center justify-center pb-16'>
          <Link
            to={`${routes.shop}`}
            className='text-4xl font-semibold font-montserrat'
          >
            Shop
          </Link>
        </div>
        <div className='flex items-center justify-between md:justify-end gap-4'>
          <Select
            options={sortOptions}
            value={sort}
            onChange={handleSortChange}
            className='w-24 md:order-last'
            // hidden={sort === 'premium'}
          />

          <div className='flex items-center justify-center gap-4'>
            <Link
              to={`${routes.shop}?premium=`}
              className={`rounded-full border border-gray-50 px-4 py-2 text-xs whitespace-nowrap text-gray-50 w-fit 
          ${premium !== '1' ? 'border-primary-200 font-bold text-primary-200' : ''}
          `}
            >
              일반 상품
            </Link>
            <Link
              to={`${routes.shop}?premium=1`}
              className={`rounded-full border border-gray-50 px-4 py-2 text-xs whitespace-nowrap text-gray-50 w-fit 
          ${premium === '1' ? 'border-primary-200 font-semibold text-primary-200' : ''}
          `}
            >
              프리미엄 선물용
            </Link>
          </div>
        </div>{' '}
        {(() => {
          if (isLoading) {
            // 데이터를 불러오는 중
            return (
              <div className='flex justify-center items-center py-20'>
                <div className='text-lg text-gray-500'>
                  상품을 불러오는 중...
                </div>
              </div>
            );
          } else if (productItems.length === 0) {
            // 데이터가 다 왔는데 상품이 없는 경우
            return (
              <div className='flex justify-center items-center py-20'>
                <div className='text-lg text-gray-500'>상품이 없습니다.</div>
              </div>
            );
          } else {
            // 상품이 있는 경우 기존 코드 유지
            return (
              <ul className='grid grid-cols-3 md:grid-cols-3 gap-1 md:gap-4 mt-4 md:mt-10'>
                {Array.isArray(productItems) &&
                  productItems.map(item => (
                    <Link
                      key={item.productId}
                      to={`${routes.shopDetail}/${item.productId}`}
                    >
                      <li className='roudned-lg'>
                        <div className='relative'>
                          <ImageSkeleton
                            src={item.mainImageUrl}
                            alt={item.name}
                            containerClassName='w-full h-[120px] md:max-w-[320px] md:h-[320px] rounded-lg'
                            imgClassName={`object-cover rounded-lg ${item.stockStatus === 0 ? 'opacity-60' : ''}`}
                            skeletonClassName='rounded-lg'
                            loading='eager'
                            decoding='async'
                          />
                          {item.stockStatus === 0 && (
                            <div className='absolute inset-0 flex items-center justify-center bg-black-50/50 rounded-lg'>
                              <span className='text-white text-xs md:text-sm font-semibold px-3 py-1.5'>
                                품절
                              </span>
                            </div>
                          )}
                        </div>
                        <div className='p-2'>
                          {!item.discountPer && (
                            <div className='gap-2 md:hidden '>
                              <div className='font-semibold whitespace-nowrap text-sm md:text-base'>
                                {item.discountPrice.toLocaleString()}원
                              </div>
                              <h3 className='truncate hover:text-gray-700 text-sm'>
                                {item.name}
                              </h3>
                            </div>
                          )}
                          {!item.discountPer && (
                            <div className='justify-between gap-2 hidden md:flex'>
                              <h3 className='truncate hover:text-gray-700'>
                                {item.name}
                              </h3>
                              <p className=' font-semibold whitespace-nowrap text-sm md:text-base'>
                                {item.discountPrice.toLocaleString()}원
                              </p>
                            </div>
                          )}
                          {item.discountPer > 0 && (
                            <div className=' flex-col gap-2 hidden md:flex'>
                              <div className='flex justify-between gap-2'>
                                <p className='hover:text-gray-700 truncate'>
                                  {item.name}
                                </p>
                                <p className='line-through text-gray-500 whitespace-nowrap hidden md:block'>
                                  {(
                                    item.basePrice + item.salesMargin
                                  ).toLocaleString()}
                                  원
                                </p>
                              </div>
                              <div className='flex justify-start md:justify-end gap-2'>
                                <p className='flex md:text-lg font-semibold text-red-500 justify-start text-sm'>
                                  {item.discountPer}%
                                </p>
                                <p className='text-sm md:text-lg font-semibold text-gray-900'>
                                  {item.discountPrice.toLocaleString()}원
                                </p>
                              </div>
                            </div>
                          )}
                          {item.discountPer > 0 && (
                            <div className='flex flex-col md:hidden'>
                              <div className='flex justify-start gap-1'>
                                <p className='flex md:text-lg font-semibold text-red-500 justify-start text-sm'>
                                  {item.discountPer}%
                                </p>
                                <p className='text-sm md:text-lg font-semibold text-gray-900 whitespace-nowrap '>
                                  {item.discountPrice.toLocaleString()}원
                                </p>
                              </div>
                              <p className='hover:text-gray-700 truncate text-sm '>
                                {item.name}
                              </p>
                            </div>
                          )}
                        </div>
                      </li>
                    </Link>
                  ))}
              </ul>
            );
          }
        })()}
        <Pagination
          totalPage={pagination.totalPage}
          currentPage={pagination.currentPage}
        />
      </InnerPaddingSectionWrapper>
    </>
  );
}
